import { useParams, Link, Navigate } from "react-router-dom";
import SEO from "@/components/SEO";
import { getService, services } from "@/data/services";
import { getCity, cities } from "@/data/cities";
import { RevealBlock } from "@/components/Reveal";

// Programmatic content generator per service × city combination.
function comboContent(service, city) {
  const short = service.shortName || service.name;
  const shortLower = short.toLowerCase();
  const nameLower = service.name.toLowerCase();
  const kw = [
    // Local combinations
    `${service.name} ${city.name}`,
    `${short} security ${city.name}`,
    `${city.name} ${nameLower}`,
    `${shortLower} security guards ${city.name}`,
    `${service.name} services ${city.name} Nova Scotia`,
    `24/7 ${nameLower} ${city.name}`,
    `licensed ${nameLower} company ${city.name}`,
    `${service.name} ${city.name} NS`,
    `${city.name} ${service.name} contractor`,
    `${city.region} ${nameLower}`,
    // Canadian and regional variants
    `${service.name} Canada ${city.name}`,
    `Canadian ${nameLower} company ${city.name}`,
    `Atlantic Canada ${nameLower} ${city.name}`,
    `${city.name} ${nameLower} Nova Scotia licensed`,
    `hire ${nameLower} in ${city.name}`,
    `professional ${nameLower} ${city.name}`,
    `certified ${nameLower} officers ${city.name}`,
    `${city.name} ${short} rates`,
    `${city.name} ${short} quote`,
    `${city.name} ${short} same-day dispatch`,
  ];

  const intro = `${service.name} in ${city.name}, Nova Scotia — delivered by uniformed, licensed officers who live and work in ${city.region}. Whether your ${city.name} operation is a permanent site, a one-week engagement, or a critical overnight shift, our ${service.shortName || service.name.toLowerCase()} team is briefed, badged, and on post before the assignment begins.`;

  const body = [
    `${city.name} carries its own operational rhythms — traffic patterns, event calendars, industrial cycles, and the seasonal weather patterns of ${city.region}. Our ${service.name.toLowerCase()} program in ${city.name} is built by supervisors who know these variables intimately, not imported from a national playbook.`,
    `Every officer assigned to a ${service.name.toLowerCase()} post in ${city.name} carries a Nova Scotia Security Guard licence, a valid Standard First Aid + CPR-C certificate, and site-specific orientation completed before the first shift. We staff to your risk profile, your operating hours, and your compliance obligations — not to a call-centre roster.`,
    `${service.intro}`,
    ...service.body.slice(0, 3),
    `In ${city.name} specifically, we adapt this program with local supervisors, ${city.region}-based dispatch, and roster continuity that eliminates the "revolving door of strangers" our clients tell us they experience with national vendors. Every daily activity report, every incident file, every monthly analytics package is delivered in writing to a named point of contact.`,
    `${service.name} in ${city.name} is billed at Nova Scotia-standard rates with no hidden mobilisation or minimum-hours fees. Contracts scale from a single overnight to full 24/7 dedicated posts. A licensed supervisor is on call for every ${city.name} client around the clock.`,
  ];

  return { keywords: kw, intro, body };
}

export default function ServiceCity() {
  const { serviceSlug, citySlug } = useParams();
  const service = getService(serviceSlug);
  const city = getCity(citySlug);

  if (!service) return <Navigate to="/services" replace />;
  if (!city) return <Navigate to={`/services/${serviceSlug}`} replace />;

  const { keywords, intro, body } = comboContent(service, city);
  const title = `${service.name} in ${city.name}, Nova Scotia`;
  const description = intro.slice(0, 180);
  const path = `/services/${service.slug}/${city.slug}`;

  // Sibling services & cities for internal linking
  const siblingServices = services.filter((s) => s.slug !== service.slug).slice(0, 6);
  const siblingCities = cities.filter((c) => c.slug !== city.slug).slice(0, 12);

  return (
    <>
      <SEO
        title={title}
        description={description}
        path={path}
        image={service.hero}
        keywords={keywords}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: title,
          serviceType: service.name,
          areaServed: {
            "@type": "City",
            name: city.name,
            containedInPlace: {
              "@type": "AdministrativeArea",
              name: "Nova Scotia",
            },
          },
          provider: {
            "@type": "SecurityService",
            name: "Nova Scotia Security Services",
          },
        }}
      />

      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-16 md:pt-20">
        <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[#111111]/60">
          <Link to="/" className="hover:text-[#C17A43]">Home</Link>
          <span>›</span>
          <Link to="/services" className="hover:text-[#C17A43]">Services</Link>
          <span>›</span>
          <Link to={`/services/${service.slug}`} className="hover:text-[#C17A43]">
            {service.name}
          </Link>
          <span>›</span>
          <Link to={`/cities/${city.slug}`} className="hover:text-[#C17A43]">
            {city.name}
          </Link>
        </div>

        <div className="grid grid-cols-12 gap-4 mt-12 md:mt-16 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-6">
              Chapter {service.number} · {city.region}
            </p>
            <RevealBlock>
              <h1 className="font-serif text-[clamp(2.5rem,8vw,8rem)] leading-[0.9] tracking-tighter text-[#111111]">
                {service.name}{" "}
                <span className="italic text-[#184C59]">in {city.name}.</span>
              </h1>
            </RevealBlock>
            <p className="mt-8 font-serif italic text-2xl md:text-3xl text-[#184C59] max-w-[36ch] leading-tight">
              {service.tagline}
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pb-6 border border-[#D9D6D0] p-8 bg-[#F5F3EF]">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43] mb-4">
              — Deliverables in {city.name}
            </p>
            <ul className="space-y-2">
              {service.deliverables?.map((d) => (
                <li key={d} className="font-sans text-sm text-[#111111]/80 flex gap-2">
                  <span className="text-[#C17A43]">✦</span> {d}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 mt-24 pb-24">
          <div className="col-span-12 lg:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] sticky top-32">
              — Local Brief
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <RevealBlock>
              <p className="font-serif italic text-3xl md:text-5xl leading-tight text-[#111111] mb-12">
                {intro}
              </p>
            </RevealBlock>
            {body.map((p, i) => (
              <RevealBlock key={i} delay={i * 0.05}>
                <p className="font-sans text-base md:text-lg leading-relaxed text-[#111111]/80 mb-6">
                  {p}
                </p>
              </RevealBlock>
            ))}

            {/* SEO keyword coverage */}
            <div className="mt-16 border-t border-[#D9D6D0] pt-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#111111]/50 mb-4">
                — Commonly searched · {city.name}
              </p>
              <div className="flex flex-wrap gap-2">
                {keywords.map((k) => (
                  <span
                    key={k}
                    className="font-mono text-[10px] uppercase tracking-[0.18em] border border-[#D9D6D0] px-3 py-1.5 text-[#111111]/70"
                  >
                    {k}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Cross-link back to service + city + siblings */}
        <div className="border-t border-[#D9D6D0] py-16 md:py-24">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-8">
            — Other disciplines in {city.name}
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 border-t border-[#111111] pt-6">
            {siblingServices.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}/${city.slug}`}
                  className="group flex items-baseline justify-between border-b border-[#D9D6D0] py-2 hover:border-[#111111]"
                >
                  <span className="font-serif text-lg text-[#111111]/80 group-hover:italic group-hover:text-[#C17A43] transition-colors">
                    {s.shortName || s.name} · {city.name}
                  </span>
                  <span className="opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-transform">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-[#D9D6D0] py-16 md:py-24">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-8">
            — {service.name} in nearby communities
          </p>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1.5 border-t border-[#111111] pt-6">
            {siblingCities.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/services/${service.slug}/${c.slug}`}
                  className="group flex items-baseline justify-between border-b border-[#D9D6D0] py-2 hover:border-[#111111]"
                >
                  <span className="font-serif text-lg text-[#111111]/80 group-hover:italic group-hover:text-[#C17A43] transition-colors">
                    {service.shortName || service.name} · {c.name}
                  </span>
                  <span className="opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-transform">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
